import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  fromUnixTime,
  getUnixTime,
  startOfMonth,
} from 'date-fns';
import {
  animationFrames,
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  throttleTime,
} from 'rxjs';
import { utcDifferenceInDays } from 'server/helpers/utcDifferenceInDays';
import { startOfDayUtc } from 'server/shared/helpers/startOfDayUtc';
import { Customer } from 'server/shared/models/customer';
import {
  DispoUser,
  EmploymentAvailabilityStatusEnum,
  PoolerUser,
} from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { PoolerCustomerMap } from 'server/shared/helpers/PoolerMap';
import { UserService } from 'src/app/services/user.service';
import { rxJsonClone } from 'src/helpers/rxJsonClone';
import { rxFilterIsNotEmpty } from '../../../../helpers/filterIsNotEmpty';
import { queryParam$ } from '../../../../helpers/queryParam$';
import { runInZone } from '../../../../helpers/runInZone';
import { CustomerService } from '../../../services/customer.service';
import { PoolerService } from '../../../services/pooler/pooler.service';
import { SubscriptionService } from '../../../shared/subscription.service';
import { EmploymentAvailabilitiesWithLength } from '../availabilities-user-list/employment-availabilities-user-list.component';
import { getEmploymentAvailabilityUsersQuery } from './getEmploymentAvailabilityUsersQuery';

export type RequestState<T> = { loading: boolean; data?: T };

export interface EmploymentAvailabilityListUser extends PoolerUser {
  customerOfUser?: Customer;
  employmentAvailabilitiesWithLength: EmploymentAvailabilitiesWithLength[];
  canEditAvailabilities: boolean;
}

@Injectable()
export class EmploymentAvailabilityManagementService implements OnDestroy {
  destroyed$ = new Subject<void>();

  defaultMonths = 8;

  departmentParam$ = queryParam$(this.route, 'department');

  fromParam$ = queryParam$(this.route, 'selectedFrom');
  fromParamDate$ = this.fromParam$.pipe(
    map((date) => (date ? new Date(date) : undefined)),
  );

  toParam$ = queryParam$(this.route, 'selectedTo');
  toParamDate$ = this.toParam$.pipe(
    map((date) => (date ? new Date(date) : undefined)),
  );

  searchParam$ = queryParam$(this.route, 'searchText').pipe(debounceTime(300));

  certificatesParam$ = queryParam$(this.route, 'certificates');

  poolerCustomerMap$ = this.poolerService.readyEmploymentPoolerCustomerMap$;
  dispoCustomer$ = this.customerService.readyDispoCustomer$;

  loggedInUser$ = this.userService.readyDispo$;
  preparedUserById = new Map<string, EmploymentAvailabilityListUser>();

  usersQuery$ = combineLatest([
    this.searchParam$,
    this.departmentParam$,
    this.certificatesParam$,
    this.poolerService.readyEmploymentSharedPoolCustomers$,
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(
      ([
        search,
        department,
        certificate,
        sharedPoolCustomers,
        currentCustomer,
      ]) => {
        return getEmploymentAvailabilityUsersQuery(
          search,
          department,
          certificate,
          currentCustomer,
          sharedPoolCustomers,
        );
      },
    ),
    takeUntil(this.destroyed$),
  );

  poolersByQuery$ = combineLatest([
    this.usersQuery$,
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    switchMap(([query, customer]) =>
      this.poolerService.getPoolers(query, customer).pipe(shareReplay(1)),
    ),
    map((poolers) =>
      // filter by users that have the publication loaded already by checking if the array field exists (not has content)
      poolers.filter((pooler) => pooler.employmentAvailabilities),
    ),
    throttleTime(200, undefined, { leading: true, trailing: true }),
    // if we don't clone & edit the avialability, the user gets duplicated in Users.find() observable somehow..
    rxJsonClone(),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );

  sortedUsers$ = combineLatest([
    this.poolersByQuery$,
    this.fromParamDate$,
    this.toParamDate$,
    this.poolerService.employmentPoolerCustomerMap$.pipe(rxFilterIsNotEmpty()),
    this.loggedInUser$,
    this.dispoCustomer$,
  ]).pipe(
    map(
      ([
        currentUsers,
        from,
        to,
        poolerCustomerMap,
        loggedInUser,
        dispoCustomer,
      ]) => {
        if (!currentUsers) return [];
        return [
          ...this.sortUsersByAvailabilities(
            currentUsers,
            from,
            to,
            poolerCustomerMap,
            loggedInUser,
            dispoCustomer,
          ),
        ];
      },
    ),
    shareReplay(1),
  );

  // Loader that processes users one by one with animation frames
  usersLoader$ = of(new Map<string, boolean>()).pipe(
    switchMap((renderedUserIds) =>
      combineLatest([
        this.sortedUsers$.pipe(rxFilterIsNotEmpty()),
        this.calendarRange$,
        this.poolerCustomerMap$,
        this.loggedInUser$,
        this.dispoCustomer$,
      ]).pipe(
        map(
          ([
            sortedUsers,
            calendarRange,
            poolerCustomerMap,
            loggedInUser,
            dispoCustomer,
          ]) => {
            return {
              users: sortedUsers,
              renderedUserIds,
              calendarRange,
              poolerCustomerMap,
              loggedInUser,
              dispoCustomer,
            };
          },
        ),
      ),
    ),
    switchMap(
      ({
        users,
        renderedUserIds,
        calendarRange,
        poolerCustomerMap,
        loggedInUser,
        dispoCustomer,
      }) =>
        // Use from() and concatMap to apply delay
        animationFrames().pipe(
          delay(100),
          map(() => ({
            usersToLoad: users.filter((user) => !renderedUserIds.has(user._id)),
          })),
          takeWhile(
            ({ usersToLoad }) => usersToLoad.length > 0,
            true, // inclusive so it emits the final state
          ),
          switchMap(async ({ usersToLoad }) => {
            // Load one user at a time
            if (usersToLoad[0]) {
              const preparedUser = this.getPreparedEmploymentAvailabilityUser(
                loggedInUser,
                dispoCustomer,
                usersToLoad[0],
                calendarRange.from,
                calendarRange.to,
                poolerCustomerMap,
              );
              this.preparedUserById.set(preparedUser._id, preparedUser);
              renderedUserIds.set(usersToLoad[0]._id, true);
            }
            return { renderedUserIds, users, loading: usersToLoad.length > 1 };
          }),
        ),
    ),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );

  loadingUsers$ = combineLatest([
    this.usersLoader$.pipe(map((loader) => loader.loading)),
    this.subscriptionService.employmentUsersReady$.pipe(startWith(false)),
  ]).pipe(
    map(([loading, subscriptionReady]) => loading || !subscriptionReady),
    runInZone(this.ngZone),
  );

  rangeParam$ = queryParam$(this.route, 'range').pipe(
    map((range) => range ?? 'ALL'),
    distinctUntilChanged(),
  );

  calendarRange$: Observable<{ from: Date; to: Date }> = this.rangeParam$.pipe(
    map((range) => {
      if (range === 'ALL' || !range) {
        return {
          from: startOfMonth(startOfDayUtc(new Date())),
          to: endOfMonth(addMonths(this.from, this.defaultMonths)),
        };
      } else {
        let splitRange = range.split(',');
        const rangeStart = new Date(splitRange[1]);
        const rangeEnd = new Date(splitRange[2]);
        return {
          from: startOfDayUtc(rangeStart),
          to: startOfDayUtc(rangeEnd),
        };
      }
    }),
    shareReplay(1),
  );

  from: Date = startOfMonth(startOfDayUtc(new Date()));
  to: Date = endOfMonth(addMonths(this.from, this.defaultMonths));

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private poolerService: PoolerService,
    private subscriptionService: SubscriptionService,
    private ngZone: NgZone,
    private customerService: CustomerService,
    private userService: UserService,
  ) {
    this.subscriptionService.subscribeEmploymentUsers();
    this.subscriptionService.employmentUsersReady$.subscribe();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  updateQueryParams(queryParams: { [key: string]: string | null }) {
    this.router
      .navigate([], {
        queryParamsHandling: 'merge',
        queryParams,
      })
      .then();
  }

  getPreparedEmploymentAvailabilityUser(
    loggedInUser: DispoUser,
    dispoCustomer: Customer,
    user: PoolerUser,
    start: Date,
    end: Date,
    poolerCustomerMap: PoolerCustomerMap,
  ): EmploymentAvailabilityListUser {
    let customerOfUser = poolerCustomerMap?.getCustomerOfUser(user._id);
    if (customerOfUser?._id === loggedInUser?.customerId) {
      customerOfUser = undefined;
    }

    const employmentAvailabilitiesWithLength: EmploymentAvailabilitiesWithLength[] =
      [];
    const calendarStartUnix = getUnixTime(start || new Date());
    const calendarEndUnix = getUnixTime(end || new Date());
    const daysByDateMap = new Map(
      user.employmentAvailabilityDays?.map((day) => [day.date, day]),
    );
    user.employmentAvailabilities?.forEach((availability) => {
      let startDateUnix = getUnixTime(new Date(availability.startDate));
      let endDateUnix = getUnixTime(new Date(availability.endDate));

      if (
        (startDateUnix < calendarStartUnix &&
          endDateUnix < calendarStartUnix) ||
        (startDateUnix > calendarEndUnix && endDateUnix > calendarEndUnix)
      ) {
        return;
      }

      if (
        startDateUnix < calendarStartUnix &&
        endDateUnix > calendarStartUnix
      ) {
        startDateUnix = calendarStartUnix;
      }

      if (endDateUnix > calendarEndUnix && startDateUnix < calendarEndUnix) {
        endDateUnix = calendarEndUnix;
      }
      const daysBetween = utcDifferenceInDays(
        fromUnixTime(endDateUnix),
        fromUnixTime(startDateUnix),
      );

      let visibleDaysStart = daysBetween + 1;

      let visibleDaysEnd = daysBetween + 1;

      for (let i = 0; i < daysBetween + 1; i++) {
        const date = addDays(fromUnixTime(startDateUnix), i);
        const dayAvailability = daysByDateMap.get(format(date, dbDayDateToken));
        if (
          dayAvailability?.status !== availability.status &&
          !availability.bookedEmployment
        ) {
          if (visibleDaysStart > i) {
            visibleDaysStart = i;
          }
          if (visibleDaysEnd > daysBetween - i) {
            visibleDaysEnd = daysBetween - i;
          }
        }
      }

      employmentAvailabilitiesWithLength.push({
        ...availability,
        daysBetween: daysBetween + 1,
        daysOffset: utcDifferenceInDays(
          fromUnixTime(startDateUnix),
          fromUnixTime(calendarStartUnix),
        ),
        calendarStart: format(fromUnixTime(startDateUnix), dbDayDateToken),
        calendarEnd: format(fromUnixTime(endDateUnix), dbDayDateToken),
        daysVisibleStart: visibleDaysStart,
        daysVisibleEnd: visibleDaysEnd,
      });
    });
    user.detailsCollapsed = true;

    return {
      ...user,
      employmentAvailabilitiesWithLength: employmentAvailabilitiesWithLength,
      customerOfUser,
      canEditAvailabilities:
        dispoCustomer?.pool.activeUserIds.includes(user._id) || false,
    };
  }

  sortUsersByAvailabilities(
    users: PoolerUser[],
    start: Date | undefined,
    end: Date | undefined,
    poolerCustomerMap: PoolerCustomerMap,
    loggedInUser: DispoUser,
    dispoCustomer: Customer,
  ): PoolerUser[] {
    if (!start || !end) {
      return (users = users?.sort((a, b) =>
        this.availabilitySort(
          a,
          b,
          poolerCustomerMap,
          loggedInUser,
          dispoCustomer,
        ),
      ));
    }
    const startString = format(start, dbDayDateToken);
    const endString = format(end, dbDayDateToken);
    const availabilitiesPerUser: { [userId: string]: number } = {};
    users.forEach((user) => {
      let availabilities = 0;
      user.employmentAvailabilityDays?.forEach((day) => {
        if (
          day.date >= startString &&
          day.date <= endString &&
          day.status === EmploymentAvailabilityStatusEnum.AVAILABLE
        ) {
          availabilities++;
        }
      });
      availabilitiesPerUser[user._id] = availabilities;
    });
    return users?.sort((a, b) => {
      return availabilitiesPerUser[b._id] - availabilitiesPerUser[a._id];
    });
  }

  availabilitySort(
    a: PoolerUser,
    b: PoolerUser,
    poolerCustomerMap: PoolerCustomerMap,
    loggedInUser: DispoUser,
    dispoCustomer: Customer,
  ) {
    const customerA = poolerCustomerMap.getCustomerOfUser(a._id);
    const customerB = poolerCustomerMap.getCustomerOfUser(b._id);
    if (customerA?._id === customerB?._id) {
      let aName = dispoCustomer.prioritizeEmployeeLastName
        ? a.lastName + ' ' + a.firstName
        : a.firstName + ' ' + a.lastName;
      let bName = dispoCustomer.prioritizeEmployeeLastName
        ? b.lastName + ' ' + b.firstName
        : b.firstName + ' ' + b.lastName;
      return aName.localeCompare(bName);
    }
    if (customerA?._id === loggedInUser?.customerId || !customerA) {
      return -1;
    }
    if (customerB?._id === loggedInUser?.customerId || !customerB) {
      return 1;
    }
    if (dispoCustomer?.sharedPoolPriorities) {
      const aPriority = dispoCustomer.sharedPoolPriorities.findIndex(
        (priority) => priority.customerId === customerA?._id,
      );
      const bPriority = dispoCustomer.sharedPoolPriorities.findIndex(
        (priority) => priority.customerId === customerB?._id,
      );
      if (aPriority === -1) {
        return 1;
      } else if (bPriority === -1) {
        return -1;
      }
      return aPriority - bPriority;
    }
    return customerB?._id.localeCompare(customerA?._id);
  }

  updateAvailability(user: PoolerUser) {
    combineLatest([
      this.poolersByQuery$,
      this.calendarRange$,
      this.loggedInUser$,
      this.dispoCustomer$,
      this.poolerCustomerMap$,
    ])
      .pipe(take(1))
      .subscribe(
        ([
          poolersByQuery,
          { from, to },
          loggedInUser,
          dispoCustomer,
          poolerCustomerMap,
        ]) => {
          const userIndex = poolersByQuery.findIndex((u) => u._id === user._id);
          if (userIndex !== -1) {
            const updatedPreparedUser =
              this.getPreparedEmploymentAvailabilityUser(
                loggedInUser,
                dispoCustomer,
                user,
                from,
                to,
                poolerCustomerMap,
              );
            // Update both the map and the array
            this.preparedUserById.set(user._id, updatedPreparedUser);
          }
          Meteor.call(
            'updateEmploymentAvailabilitiesByClient',
            user._id,
            user.employmentAvailabilities,
            user.employmentAvailabilityDays,
          );
        },
      );
  }
}
